import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiSave, 
  FiX, 
  FiPlus, 
  FiTrash2, 
  FiUser, 
  FiFileText, 
  FiActivity,
  FiClipboard,
  FiHeart,
  FiDroplet
} from 'react-icons/fi';
import { BsTrash, BsPencil } from "react-icons/bs";
import axios from 'axios';

// Import the API endpoints from your urlHelper
import { 
  API_BASE_URL, 
  API_PATIENT_LIST, 
  API_DOCTOR_LIST, 
  API_INVENTORY,
  API_BASE_HEALTH_CENTER_URL,
  API_HEALTH_CENTERS,
  API_TREATMENTM_LIST,
  API_LAB_TEMPLATES_LIST
} from '../../../../../helpers/urlHelper'; // Adjust path as needed

const PrescriptionForm = ({ 
  initialData = null, 
  onSubmit, 
  onCancel, 
  isLoading = false, 
  mode = 'create', // 'create' or 'edit'
  activePatient = null, // The active patient - no need to select
  // Remove these props since we'll fetch the data
  // doctors = [],
  // clinics = [],
  // medicines = [],
  // diseases = [],
  // labtests = []
}) => {
  // Initial states matching the offcanvas component
  const EMPTY_PRESCRIPTION_STATE = {
    doctorId: '',
    clinicId: '',
    symptoms: '',
    suggestion: '',
    condition: {
      conditionname: '',
      currentstatus: 'ongoing',
      reportedon: new Date().toISOString().split('T')[0],
      resolvedon: '',
      comments: ''
    },
    diseases: [],
    medicines: [],
    labtests: []
  };

  const INITIAL_DISEASE_STATE = {
    diseaseId: '',
    name: '',
    description: '',
    severity: 'moderate',
    duration: ''
  };

  const INITIAL_MEDICINE_STATE = {
    medicineId: '',
    name: '',
    dosage: '',
    frequency: '',
    duration: '',
    purpose: ''
  };

  const INITIAL_LABTEST_STATE = {
    labtestId: '',
    name: '',
    result: '',
    date: new Date().toISOString().split('T')[0]
  };

  // Form state
  const [formData, setFormData] = useState(EMPTY_PRESCRIPTION_STATE);
  const [disease, setDisease] = useState(INITIAL_DISEASE_STATE);
  const [medicine, setMedicine] = useState(INITIAL_MEDICINE_STATE);
  const [labtest, setLabTest] = useState(INITIAL_LABTEST_STATE);

  // ADD: Data states for dropdowns
  const [doctors, setDoctors] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [diseases, setDiseases] = useState([]);
  const [labtests, setLabtests] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  // Editing states
  const [editingDiseaseId, setEditingDiseaseId] = useState(null);
  const [editingMedicineId, setEditingMedicineId] = useState(null);
  const [editingLabTestId, setEditingLabTestId] = useState(null);

  // Validation errors
  const [errors, setErrors] = useState({});

  // ADD: Fetch functions from the offcanvas component
  const fetchDoctors = async () => {
    try {
      const response = await fetch(`${API_DOCTOR_LIST}`);
      if (!response.ok) throw new Error('Error al obtener médicos');
      const data = await response.json();
      return data?.data || [];
    } catch (err) {
      console.error('Error fetching doctors:', err);
      return [];
    }
  };

  const fetchMedicines = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}${API_INVENTORY}`);
      return response.data || [];
    } catch (err) {
      console.error('Error fetching medicines:', err);
      return [];
    }
  };

  const fetchClinics = async () => {
    try {
      console.log('Fetching clinics from:', `${API_BASE_HEALTH_CENTER_URL}${API_HEALTH_CENTERS}`);
      const response = await fetch(`${API_BASE_HEALTH_CENTER_URL}${API_HEALTH_CENTERS}`);
      console.log('Clinics response status:', response.status);
      
      if (!response.ok) {
        console.error('Clinics fetch failed:', response.status, response.statusText);
        throw new Error(`Error al obtener clínicas: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Clinics raw response:', data);
      console.log('Clinics processed data:', data?.data || data || []);
      
      return data?.data || data || [];
    } catch (err) {
      console.error('Error fetching clinics:', err);
      return [];
    }
  };

  const fetchDiseases = async () => {
    try {
      console.log('Fetching diseases from:', `${API_BASE_URL}${API_TREATMENTM_LIST}`);
      const response = await fetch(`${API_BASE_URL}${API_TREATMENTM_LIST}`);
      console.log('Diseases response status:', response.status);
      
      if (!response.ok) {
        console.error('Diseases fetch failed:', response.status, response.statusText);
        throw new Error(`Error al obtener enfermedades: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Diseases raw response:', data);
      
      let processedData = [];
      
      // Caso 1: Si es un objeto con template directamente
      if (data.template && data.template.name) {
        processedData = [{
          _id: data._id || Date.now().toString(),
          name: data.template.name,
          diseaseName: data.template.name,
          purpose: data.template.purpose || '',
          protocol_code: data.template.protocol_code || '',
          specialty_id: data.template.specialty_id || '',
          status: data.template.status || ''
        }];
      }
      // Caso 2: Si es un array de objetos con template
      else if (Array.isArray(data)) {
        processedData = data.map(item => ({
          _id: item._id || Date.now().toString(),
          name: item.template?.name || item.name || item.diseaseName || 'Sin nombre',
          diseaseName: item.template?.name || item.diseaseName || item.name || 'Sin nombre',
          purpose: item.template?.purpose || item.purpose || '',
          protocol_code: item.template?.protocol_code || item.protocol_code || '',
          specialty_id: item.template?.specialty_id || item.specialty_id || '',
          status: item.template?.status || item.status || ''
        }));
      }
      // Caso 3: Si tiene data como array
      else if (data?.data && Array.isArray(data.data)) {
        processedData = data.data.map(item => ({
          _id: item._id || Date.now().toString(),
          name: item.template?.name || item.name || item.diseaseName || 'Sin nombre',
          diseaseName: item.template?.name || item.diseaseName || item.name || 'Sin nombre',
          purpose: item.template?.purpose || item.purpose || '',
          protocol_code: item.template?.protocol_code || item.protocol_code || '',
          specialty_id: item.template?.specialty_id || item.specialty_id || '',
          status: item.template?.status || item.status || ''
        }));
      }
      // Caso 4: Respuesta directa con data que contiene template
      else if (data?.data?.template?.name) {
        processedData = [{
          _id: data.data._id || data._id || Date.now().toString(),
          name: data.data.template.name,
          diseaseName: data.data.template.name,
          purpose: data.data.template.purpose || '',
          protocol_code: data.data.template.protocol_code || '',
          specialty_id: data.data.template.specialty_id || '',
          status: data.data.template.status || ''
        }];
      }
      
      console.log('Diseases processed data:', processedData);
      return processedData;
      
    } catch (err) {
      console.error('Error fetching diseases:', err);
      return [];
    }
  };

  const fetchLabTests = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}${API_LAB_TEMPLATES_LIST}`);
      if (!response.ok) throw new Error('Error al obtener exámenes de laboratorio');
      const data = await response.json();
      return data?.data || [];
    } catch (err) {
      console.error('Error fetching lab tests:', err);
      return [];
    }
  };

  // MOVED ALL useCallback HOOKS TO HERE - BEFORE ANY CONDITIONAL RETURNS

  // Validation function
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.doctorId) {
      newErrors.doctorId = 'Doctor is required';
    }

    if (!formData.clinicId) {
      newErrors.clinicId = 'Clinic is required';
    }

    if (!formData.symptoms?.trim()) {
      newErrors.symptoms = 'Symptoms are required';
    }

    if (formData.diseases.length === 0 && formData.medicines.length === 0 && formData.labtests.length === 0) {
      newErrors.general = 'At least one disease, medicine, or lab test is required';
    }

    // Validate diseases
    formData.diseases.forEach((disease, index) => {
      if (!disease.name?.trim()) {
        newErrors[`disease_${index}`] = 'Disease name is required';
      }
    });

    // Validate medicines
    formData.medicines.forEach((medicine, index) => {
      if (!medicine.name?.trim()) {
        newErrors[`medicine_${index}_name`] = 'Medicine name is required';
      }
    });

    // Validate lab tests
    formData.labtests.forEach((test, index) => {
      if (!test.name?.trim()) {
        newErrors[`labtest_${index}`] = 'Lab test name is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Handle input changes
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear specific field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
    if (errors.general) {
      setErrors(prev => ({ ...prev, general: undefined }));
    }
  }, [errors]);

  const handleConditionChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      condition: {
        ...prev.condition,
        [name]: value
      }
    }));
  }, []);

  const handleDiseaseChange = useCallback((e) => {
    const { name, value } = e.target;
    
    if (name === 'diseaseId' && value) {
      const selectedDisease = diseases.find(d => d._id === value);
      if (selectedDisease) {
        setDisease(prev => ({ 
          ...prev, 
          diseaseId: value,
          name: selectedDisease.name || selectedDisease.diseaseName || selectedDisease.template?.name || ''
        }));
        return;
      }
    }
    
    setDisease(prev => ({ ...prev, [name]: value }));
  }, [diseases]);

  const handleMedicineChange = useCallback((e) => {
  const { name, value } = e.target;
  
  if (name === 'medicineId' && value) {
    const selectedMedicine = medicines.find(m => m._id === value);
    if (selectedMedicine) {
      setMedicine(prev => ({ 
        ...prev, 
        medicineId: value,
        name: selectedMedicine.name || selectedMedicine.medicineName || ''
      }));
      return;
    }
  }
  
  setMedicine(prev => ({ ...prev, [name]: value }));
}, [medicines]);

// Reemplaza el handleLabTestChange existente con este:
const handleLabTestChange = useCallback((e) => {
  const { name, value } = e.target;
  
  if (name === 'labtestId' && value) {
    const selectedLabtest = labtests.find(l => l._id === value);
    if (selectedLabtest) {
      setLabTest(prev => ({ 
        ...prev, 
        labtestId: value,
        name: selectedLabtest.name || selectedLabtest.testName || ''
      }));
      return;
    }
  }
  
  setLabTest(prev => ({ ...prev, [name]: value }));
}, [labtests]);

  // Disease management
  const addDisease = useCallback(() => {
    if (!disease.name && !disease.diseaseId) {
      alert('Please complete the disease name');
      return;
    }

    if (editingDiseaseId !== null) {
      setFormData(prev => ({
        ...prev,
        diseases: prev.diseases.map(d => 
          d.id === editingDiseaseId ? { ...disease, id: editingDiseaseId } : d
        )
      }));
      setEditingDiseaseId(null);
    } else {
      setFormData(prev => ({
        ...prev,
        diseases: [...prev.diseases, { ...disease, id: Date.now() }]
      }));
    }
    setDisease(INITIAL_DISEASE_STATE);
  }, [disease, editingDiseaseId, INITIAL_DISEASE_STATE]);

  const editDisease = useCallback((id) => {
    const diseaseToEdit = formData.diseases.find(d => d.id === id);
    if (diseaseToEdit) {
      setDisease(diseaseToEdit);
      setEditingDiseaseId(id);
    }
  }, [formData.diseases]);

  const removeDisease = useCallback((id) => {
    setFormData(prev => ({
      ...prev,
      diseases: prev.diseases.filter(d => d.id !== id)
    }));
    if (editingDiseaseId === id) {
      setDisease(INITIAL_DISEASE_STATE);
      setEditingDiseaseId(null);
    }
  }, [editingDiseaseId, INITIAL_DISEASE_STATE]);

  // Medicine management
  const addMedicine = useCallback(() => {
    if (!medicine.name) {
      alert('Please complete the medicine name');
      return;
    }

    if (editingMedicineId !== null) {
      setFormData(prev => ({
        ...prev,
        medicines: prev.medicines.map(m => 
          m.id === editingMedicineId ? { ...medicine, id: editingMedicineId } : m
        )
      }));
      setEditingMedicineId(null);
    } else {
      setFormData(prev => ({
        ...prev,
        medicines: [...prev.medicines, { ...medicine, id: Date.now() }]
      }));
    }
    setMedicine(INITIAL_MEDICINE_STATE);
  }, [medicine, editingMedicineId, INITIAL_MEDICINE_STATE]);

  const editMedicine = useCallback((id) => {
    const medicineToEdit = formData.medicines.find(m => m.id === id);
    if (medicineToEdit) {
      setMedicine(medicineToEdit);
      setEditingMedicineId(id);
    }
  }, [formData.medicines]);

  const removeMedicine = useCallback((id) => {
    setFormData(prev => ({
      ...prev,
      medicines: prev.medicines.filter(m => m.id !== id)
    }));
    if (editingMedicineId === id) {
      setMedicine(INITIAL_MEDICINE_STATE);
      setEditingMedicineId(null);
    }
  }, [editingMedicineId, INITIAL_MEDICINE_STATE]);

  // Lab test management
  const addLabTest = useCallback(() => {
    if (!labtest.name) {
      alert('Please complete the lab test name');
      return;
    }

    if (editingLabTestId !== null) {
      setFormData(prev => ({
        ...prev,
        labtests: prev.labtests.map(l => 
          l.id === editingLabTestId ? { ...labtest, id: editingLabTestId } : l
        )
      }));
      setEditingLabTestId(null);
    } else {
      setFormData(prev => ({
        ...prev,
        labtests: [...prev.labtests, { ...labtest, id: Date.now() }]
      }));
    }
    setLabTest(INITIAL_LABTEST_STATE);
  }, [labtest, editingLabTestId, INITIAL_LABTEST_STATE]);

  const editLabTest = useCallback((id) => {
    const labtestToEdit = formData.labtests.find(l => l.id === id);
    if (labtestToEdit) {
      setLabTest(labtestToEdit);
      setEditingLabTestId(id);
    }
  }, [formData.labtests]);

  const removeLabTest = useCallback((id) => {
    setFormData(prev => ({
      ...prev,
      labtests: prev.labtests.filter(l => l.id !== id)
    }));
    if (editingLabTestId === id) {
      setLabTest(INITIAL_LABTEST_STATE);
      setEditingLabTestId(null);
    }
  }, [editingLabTestId, INITIAL_LABTEST_STATE]);

  // Handle form submission
// Handle form submission
const handleSubmit = useCallback(async (e) => {
  e.preventDefault();
  
  console.log('Form submission started');
  console.log('Form data:', formData);
  console.log('Active patient:', activePatient);

  if (!validateForm()) {
    console.log('Form validation failed:', errors);
    return;
  }

  if (!activePatient) {
    setErrors({ general: 'No active patient found' });
    return;
  }

  try {
    const submitData = {
      doctor: formData.doctorId,
      patient: activePatient._id || activePatient.patientId, // Handle both possible ID fields
      clinic: formData.clinicId,
      symptoms: formData.symptoms.trim(),
      suggestion: formData.suggestion.trim(),
      condition: formData.condition.conditionname ? formData.condition : null,
      diseases: formData.diseases.map(disease => ({
        disease: disease.diseaseId || null,
        name: disease.name,
        description: disease.description,
        severity: disease.severity,
        duration: disease.duration
      })),
      medicines: formData.medicines.map(medicine => ({
        medicine: medicine.medicineId || null,
        name: medicine.name,
        dosage: medicine.dosage,
        frequency: medicine.frequency,
        duration: medicine.duration,
        purpose: medicine.purpose
      })),
      labtests: formData.labtests.map(labtest => ({
        labtest: labtest.labtestId || null,
        name: labtest.name,
        result: labtest.result,
        date: labtest.date
      }))
    };

    console.log('Submitting cleaned data:', submitData);
    await onSubmit(submitData);
    console.log('Form submitted successfully');
  } catch (error) {
    console.error('Form submission error:', error);
    setErrors({ general: error.message || 'Error submitting form' });
  }
}, [formData, validateForm, errors, activePatient, onSubmit]);

  // ADD: Load dropdown data on component mount
  useEffect(() => {
    const loadDropdownData = async () => {
      setDataLoading(true);
      try {
        const [
          doctorsData,
          medicinesData,
          clinicsData,
          diseasesData,
          labtestsData
        ] = await Promise.all([
          fetchDoctors(),
          fetchMedicines(),
          fetchClinics(),
          fetchDiseases(),
          fetchLabTests()
        ]);
        
        setDoctors(doctorsData);
        setMedicines(medicinesData);
        setClinics(clinicsData);
        setDiseases(diseasesData);
        setLabtests(labtestsData);
        
        console.log('Loaded dropdown data:', {
          doctors: doctorsData.length,
          medicines: medicinesData.length,
          clinics: clinicsData.length,
          diseases: diseasesData.length,
          labtests: labtestsData.length
        });
      } catch (err) {
        console.error('Error loading dropdown data:', err);
        setErrors(prev => ({ ...prev, general: 'Error loading form data' }));
      } finally {
        setDataLoading(false);
      }
    };

    loadDropdownData();
  }, []);

  // Initialize form data when component mounts or initialData changes
  useEffect(() => {
    if (initialData) {
      console.log('Initializing form with data:', initialData);
      setFormData({
        doctorId: initialData.doctorId || initialData.doctor?._id || '',
        clinicId: initialData.clinicId || initialData.clinic?._id || '',
        symptoms: initialData.symptoms || '',
        suggestion: initialData.suggestion || '',
        condition: {
          conditionname: initialData.condition?.conditionname || initialData.condition?.name || '',
          currentstatus: initialData.condition?.currentstatus || 'ongoing',
          reportedon: initialData.condition?.reportedon || new Date().toISOString().split('T')[0],
          resolvedon: initialData.condition?.resolvedon || '',
          comments: initialData.condition?.comments || ''
        },
        diseases: initialData.diseases || [],
        medicines: initialData.medicines || [],
        labtests: initialData.labtests || []
      });
    } else {
      setFormData(EMPTY_PRESCRIPTION_STATE);
    }
    setErrors({});
  }, [initialData, mode]);

  // ADD: Loading spinner component
  const LoadingSpinner = () => (
    <div className="d-flex justify-content-center my-3">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );

  // Show loading spinner while data is loading - MOVED AFTER ALL HOOKS
  if (dataLoading) {
    return (
      <div className="prescription-form">
        <LoadingSpinner />
        <p className="text-center text-muted">Loading form data...</p>
      </div>
    );
  }

  return (
    
    <form onSubmit={handleSubmit} className="prescription-form">
      {/* General Error */}
      {errors.general && (
        <div className="alert alert-danger mb-3">
          <strong>Error:</strong> {errors.general}
        </div>
      )}

      {/* Active Patient Display */}
      
          
                  {/* Patient Info */}
                  <div className="col-12">
                    <div className="alert alert-info">
                      <FiUser className="me-2" />
                      Adding Presctiption for: <strong>{activePatient.patientName}</strong>
                    </div>
                  </div>
          
      

      {/* Basic Information Section */}
      <div className="card mb-3 col-12">
        <div className="card-header">
          <h6 className="card-title mb-0">
            <FiClipboard className="me-2" />
            Basic Details
          </h6>
        </div>
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label required">
              <FiUser className="me-1" />
              Doctor *
            </label>
            <select
              className={`form-select ${errors.doctorId ? 'is-invalid' : ''}`}
              name="doctorId"
              value={formData.doctorId}
              onChange={handleInputChange}
              disabled={isLoading}
              required
            >
              <option value="">Select doctor</option>
              {doctors.map(doctor => (
                <option key={doctor._id} value={doctor._id}>
                  Dr. {doctor.name} {doctor.surname || doctor.lastName || ''}
                </option>
              ))}
            </select>
            {errors.doctorId && (
              <div className="invalid-feedback">{errors.doctorId}</div>
            )}
          </div>

          <div className="mb-3">
            <label className="form-label required">
              <FiActivity className="me-1" />
              Clinic *
            </label>
            <select
              className={`form-select ${errors.clinicId ? 'is-invalid' : ''}`}
              name="clinicId"
              value={formData.clinicId}
              onChange={handleInputChange}
              disabled={isLoading}
              required
            >
              <option value="">Select clinic</option>
              {clinics.map(clinic => (
                <option key={clinic._id} value={clinic._id}>
                  {clinic.name}
                </option>
              ))}
            </select>
            {errors.clinicId && (
              <div className="invalid-feedback">{errors.clinicId}</div>
            )}
          </div>

          {/* Symptoms */}
          <div className="mb-3">
            <label className="form-label required">
              <FiActivity className="me-1" />
              Symptoms *
            </label>
            <textarea
              className={`form-control ${errors.symptoms ? 'is-invalid' : ''}`}
              rows="3"
              placeholder="Describe the patient's symptoms..."
              name="symptoms"
              value={formData.symptoms}
              onChange={handleInputChange}
              disabled={isLoading}
            />
            {errors.symptoms && (
              <div className="invalid-feedback">{errors.symptoms}</div>
            )}
          </div>

          {/* Doctor's Suggestions */}
          <div className="mb-3">
            <label className="form-label">
              <FiFileText className="me-1" />
              Doctor's Suggestions
            </label>
            <textarea
              className="form-control"
              rows="3"
              placeholder="Additional suggestions or notes..."
              name="suggestion"
              value={formData.suggestion}
              onChange={handleInputChange}
              disabled={isLoading}
            />
          </div>
        </div>
      </div>

      {/* Medical Condition Section */}
      <div className="card mb-3">
        <div className="card-header">
          <h6 className="card-title mb-0">
            <FiHeart className="me-2" />
            Medical Condition
          </h6>
        </div>
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">Condition Name</label>
            <input
              type="text"
              className="form-control"
              name="conditionname"
              value={formData.condition.conditionname}
              onChange={handleConditionChange}
              placeholder="e.g. Arterial Hypertension, Type 2 Diabetes"
              disabled={isLoading}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Current Status</label>
            <select
              className="form-select"
              name="currentstatus"
              value={formData.condition.currentstatus}
              onChange={handleConditionChange}
              disabled={isLoading}
            >
              <option value="ongoing">Ongoing</option>
              <option value="resolved">Resolved</option>
              <option value="observation">Under Observation</option>
            </select>
          </div>

          <div className="mb-3">
            <label className="form-label">Reported Date</label>
            <input
              type="date"
              className="form-control"
              name="reportedon"
              value={formData.condition.reportedon}
              onChange={handleConditionChange}
              disabled={isLoading}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Resolved Date</label>
            <input
              type="date"
              className="form-control"
              name="resolvedon"
              value={formData.condition.resolvedon || ''}
              onChange={handleConditionChange}
              disabled={isLoading || formData.condition.currentstatus !== 'resolved'}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Comments</label>
            <textarea
              className="form-control"
              name="comments"
              value={formData.condition.comments}
              onChange={handleConditionChange}
              rows="3"
              placeholder="Additional comments about the medical condition"
              disabled={isLoading}
            />
          </div>
        </div>
      </div>

      {/* Diseases Section */}
      <div className="card mb-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h6 className="card-title mb-0">
            <FiHeart className="me-2" />
            Diseases ({formData.diseases.length})
          </h6>
        </div>
        <div className="card-body">
          <div className="bg-light p-3 rounded mb-3">
            <div className="mb-3">
              <label className="form-label">Disease</label>
              <select
                className="form-select"
                name="diseaseId"
                value={disease.diseaseId}
                onChange={handleDiseaseChange}
                disabled={isLoading}
              >
                <option value="">Select disease</option>
                {diseases.map(d => (
                  <option key={d._id} value={d._id}>
                    {d.name || d.diseaseName || 'Sin nombre'}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Disease Name</label>
              <input
                type="text"
                className="form-control"
                name="name"
                value={disease.name}
                onChange={handleDiseaseChange}
                placeholder="Enter disease name"
                disabled={isLoading}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Severity</label>
              <select
                className="form-select"
                name="severity"
                value={disease.severity}
                onChange={handleDiseaseChange}
                disabled={isLoading}
              >
                <option value="mild">Mild</option>
                <option value="moderate">Moderate</option>
                <option value="severe">Severe</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Duration</label>
              <input
                type="text"
                className="form-control"
                name="duration"
                value={disease.duration}
                onChange={handleDiseaseChange}
                placeholder="e.g. 3 days, 1 week"
                disabled={isLoading}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                name="description"
                value={disease.description}
                onChange={handleDiseaseChange}
                rows="2"
                placeholder="Additional notes about the disease"
                disabled={isLoading}
              />
            </div>

            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-primary"
                onClick={addDisease}
                disabled={isLoading}
              >
                <FiPlus className="me-1" />
                {editingDiseaseId !== null ? 'Update Disease' : 'Add Disease'}
              </button>
              {editingDiseaseId !== null && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setDisease(INITIAL_DISEASE_STATE);
                    setEditingDiseaseId(null);
                  }}
                  disabled={isLoading}
                >
                  <FiX className="me-1" />
                  Cancel
                </button>
              )}
            </div>
          </div>

          {/* Disease List */}
          {formData.diseases.length > 0 && (
            <div>
              <h6>Added Diseases:</h6>
              {formData.diseases.map((disease, index) => (
                <div key={disease.id} className="border rounded p-3 mb-2 bg-white">
                  {errors[`disease_${index}`] && (
                    <div className="text-danger small mb-1">{errors[`disease_${index}`]}</div>
                  )}
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <h6 className="mb-1">{disease.name}</h6>
                      <p className="text-muted small mb-1">
                        Severity: <span className="badge bg-secondary">{disease.severity}</span>
                        {disease.duration && <span className="ms-2">Duration: {disease.duration}</span>}
                      </p>
                      {disease.description && (
                        <p className="text-muted small mb-0">{disease.description}</p>
                      )}
                    </div>
                    <div className="d-flex gap-1">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => editDisease(disease.id)}
                        disabled={isLoading}
                      >
                        <BsPencil />
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => removeDisease(disease.id)}
                        disabled={isLoading}
                      >
                        <BsTrash />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Medicines Section */}
      <div className="card mb-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h6 className="card-title mb-0">
            <FiDroplet className="me-2" />
            Medicines ({formData.medicines.length})
          </h6>
        </div>
        <div className="card-body">
          <div className="bg-light p-3 rounded mb-3">
            <div className="mb-3">
              <label className="form-label">Medicine</label>
              <select
                className="form-select"
                name="medicineId"
                value={medicine.medicineId}
                onChange={handleMedicineChange}
                disabled={isLoading}
              >
                <option value="">Select medicine</option>
                {medicines.map(m => (
                  <option key={m._id} value={m._id}>
                    {m.name || m.medicineName || 'Sin nombre'}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Medicine Name</label>
              <input
                type="text"
                className="form-control"
                name="name"
                value={medicine.name}
                onChange={handleMedicineChange}
                placeholder="Enter medicine name"
                disabled={isLoading}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Dosage</label>
              <input
                type="text"
                className="form-control"
                name="dosage"
                value={medicine.dosage}
                onChange={handleMedicineChange}
                placeholder="e.g. 500mg, 2 tablets"
                disabled={isLoading}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Frequency</label>
              <input
                type="text"
                className="form-control"
                name="frequency"
                value={medicine.frequency}
                onChange={handleMedicineChange}
                placeholder="e.g. 3 times daily"
                disabled={isLoading}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Duration</label>
              <input
                type="text"
                className="form-control"
                name="duration"
                value={medicine.duration}
                onChange={handleMedicineChange}
                placeholder="e.g. 7 days, 2 weeks"
                disabled={isLoading}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Purpose</label>
              <textarea
                className="form-control"
                name="purpose"
                value={medicine.purpose}
                onChange={handleMedicineChange}
                rows="2"
                placeholder="Purpose or instructions for this medicine"
                disabled={isLoading}
              />
            </div>

            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-primary"
                onClick={addMedicine}
                disabled={isLoading}
              >
                <FiPlus className="me-1" />
                {editingMedicineId !== null ? 'Update Medicine' : 'Add Medicine'}
              </button>
              {editingMedicineId !== null && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setMedicine(INITIAL_MEDICINE_STATE);
                    setEditingMedicineId(null);
                  }}
                  disabled={isLoading}
                >
                  <FiX className="me-1" />
                  Cancel
                </button>
              )}
            </div>
          </div>

          {/* Medicine List */}
          {formData.medicines.length > 0 && (
            <div>
              <h6>Added Medicines:</h6>
              {formData.medicines.map((medicine, index) => (
                <div key={medicine.id} className="border rounded p-3 mb-2 bg-white">
                  {errors[`medicine_${index}_name`] && (
                    <div className="text-danger small mb-1">{errors[`medicine_${index}_name`]}</div>
                  )}
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <h6 className="mb-1">{medicine.name}</h6>
                      <p className="text-muted small mb-1">
                        {medicine.dosage && <span>Dosage: {medicine.dosage}</span>}
                        {medicine.frequency && <span className="ms-2">Frequency: {medicine.frequency}</span>}
                        {medicine.duration && <span className="ms-2">Duration: {medicine.duration}</span>}
                      </p>
                      {medicine.purpose && (
                        <p className="text-muted small mb-0">Purpose: {medicine.purpose}</p>
                      )}
                    </div>
                    <div className="d-flex gap-1">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => editMedicine(medicine.id)}
                        disabled={isLoading}
                      >
                        <BsPencil />
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => removeMedicine(medicine.id)}
                        disabled={isLoading}
                      >
                        <BsTrash />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Lab Tests Section */}
      <div className="card mb-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h6 className="card-title mb-0">
            <FiActivity className="me-2" />
            Lab Tests ({formData.labtests.length})
          </h6>
        </div>
        <div className="card-body">
          <div className="bg-light p-3 rounded mb-3">
            <div className="mb-3">
              <label className="form-label">Lab Test</label>
              <select
                className="form-select"
                name="labtestId"
                value={labtest.labtestId}
                onChange={handleLabTestChange}
                disabled={isLoading}
              >
                <option value="">Select lab test</option>
                {labtests.map(l => (
                  <option key={l._id} value={l._id}>
                    {l.name || l.testName || 'Sin nombre'}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">Test Name</label>
              <input
                type="text"
                className="form-control"
                name="name"
                value={labtest.name}
                onChange={handleLabTestChange}
                placeholder="Enter test name"
                disabled={isLoading}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Result</label>
              <input
                type="text"
                className="form-control"
                name="result"
                value={labtest.result}
                onChange={handleLabTestChange}
                placeholder="Test result or status"
                disabled={isLoading}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Test Date</label>
              <input
                type="date"
                className="form-control"
                name="date"
                value={labtest.date}
                onChange={handleLabTestChange}
                disabled={isLoading}
              />
            </div>

            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-primary"
                onClick={addLabTest}
                disabled={isLoading}
              >
                <FiPlus className="me-1" />
                {editingLabTestId !== null ? 'Update Lab Test' : 'Add Lab Test'}
              </button>
              {editingLabTestId !== null && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setLabTest(INITIAL_LABTEST_STATE);
                    setEditingLabTestId(null);
                  }}
                  disabled={isLoading}
                >
                  <FiX className="me-1" />
                  Cancel
                </button>
              )}
            </div>
          </div>

          {/* Lab Test List */}
          {formData.labtests.length > 0 && (
            <div>
              <h6>Added Lab Tests:</h6>
              {formData.labtests.map((labtest, index) => (
                <div key={labtest.id} className="border rounded p-3 mb-2 bg-white">
                  {errors[`labtest_${index}`] && (
                    <div className="text-danger small mb-1">{errors[`labtest_${index}`]}</div>
                  )}
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <h6 className="mb-1">{labtest.name}</h6>
                      <p className="text-muted small mb-1">
                        Date: {labtest.date}
                        {labtest.result && <span className="ms-2">Result: {labtest.result}</span>}
                      </p>
                    </div>
                    <div className="d-flex gap-1">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => editLabTest(labtest.id)}
                        disabled={isLoading}
                      >
                        <BsPencil />
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => removeLabTest(labtest.id)}
                        disabled={isLoading}
                      >
                        <BsTrash />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Form Actions */}
      <div className="d-flex justify-content-end gap-2 mt-4">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          <FiX className="me-1" />
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              {mode === 'edit' ? 'Updating...' : 'Creating...'}
            </>
          ) : (
            <>
              <FiSave className="me-1" />
              {mode === 'edit' ? 'Update Prescription' : 'Create Prescription'}
            </>
          )}
        </button>
      </div>
    </form>
   
  );
};

export default PrescriptionForm;